import { ArrowDownIcon, ArrowUpDownIcon, ArrowUpIcon } from 'lucide-react'
import * as React from 'react'

import { EmptyState } from '@ui/components/empty-state'
import { ErrorState } from '@ui/components/error-state'
import { LoadingSkeleton } from '@ui/components/loading-skeleton'
import { Pagination } from '@ui/components/pagination'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@ui/components/ui/table'
import { cn } from '@ui/lib/utils'

export type SortDirection = 'asc' | 'desc'

/** Aktuelle Sortierung. Sortiert wird serverseitig — die Tabelle meldet nur. */
export interface DataTableSort {
  key: string
  direction: SortDirection
}

/** Seitenzustand. Auch das Blättern passiert serverseitig. */
export interface DataTablePagination {
  /** 1-basiert. */
  page: number
  pageSize: number
  /** Gesamtzahl der Datensätze über alle Seiten. */
  total: number
}

export interface DataTableColumn<TRow> {
  /** Stabiler Schlüssel. Wird auch als Sortierschlüssel an den Server gemeldet. */
  key: string
  /** Spaltenüberschrift. */
  header: React.ReactNode
  /** Inhalt einer Zelle. */
  cell: (row: TRow) => React.ReactNode
  /** Lässt die Überschrift zum Sortierknopf werden. */
  sortable?: boolean
  /** Verlässlicher Text für die mobile Sortierauswahl bei komplexen Überschriften. */
  mobileSortLabel?: string
  /** Zahlen gehören nach rechts. */
  align?: 'left' | 'right'
  /** Zusätzliche Klassen für Kopf- und Datenzellen dieser Spalte. */
  className?: string
}

export interface DataTableProps<TRow> {
  columns: ReadonlyArray<DataTableColumn<TRow>>
  rows: ReadonlyArray<TRow>
  /** Eindeutiger Schlüssel je Zeile — nicht der Index. */
  rowKey: (row: TRow) => string
  /** Kurze Beschreibung der Tabelle für Screenreader. */
  caption?: string
  /** Zeigt statt der Zeilen ein Ladeskelett. */
  isLoading?: boolean
  /** Zeigt statt der Zeilen einen Fehlerzustand. */
  error?: { description?: string; details?: string; onRetry?: () => void } | null
  /** Wird gezeigt, wenn es keine Zeilen gibt. Standard: neutraler leerer Zustand. */
  emptyState?: React.ReactNode
  sort?: DataTableSort
  onSortChange?: (sort: DataTableSort) => void
  pagination?: DataTablePagination
  onPageChange?: (page: number) => void
  /** Fachliche mobile Zeilendarstellung; ohne sie bleibt die Tabelle scrollbar. */
  renderMobileRow?: (row: TRow) => React.ReactNode
  /** Macht Zeilen anklickbar (Maus, Enter und Leertaste). */
  onRowClick?: (row: TRow) => void
  className?: string
}

/**
 * Tabelle mit serverseitiger Sortierung und Pagination, Ladeskelett, leerem
 * Zustand und Fehlerzustand. Deckt den Normalfall einer Listenseite ab, ohne
 * dass ein Modul eigene Tabellenlogik bauen muss.
 *
 * Sortierung und Pagination sind bewusst *gesteuert*: die Tabelle hält keinen
 * eigenen Zustand, sondern meldet Wünsche nach oben. So bleiben URL, Query und
 * Anzeige immer synchron.
 *
 * @example
 * ```tsx
 * const [sort, setSort] = useState<DataTableSort>({ key: 'nummer', direction: 'asc' })
 * const [page, setPage] = useState(1)
 * const query = useQuery({ queryKey: ['vorgaenge', sort, page], queryFn: ... })
 *
 * <DataTable
 *   caption="Vorgänge"
 *   columns={[
 *     { key: 'nummer', header: 'Nummer', cell: (v) => v.nummer, sortable: true },
 *     { key: 'titel', header: 'Titel', cell: (v) => v.titel, sortable: true },
 *     { key: 'menge', header: 'Menge', cell: (v) => v.menge, align: 'right' },
 *   ]}
 *   rows={query.data?.items ?? []}
 *   rowKey={(v) => v.id}
 *   isLoading={query.isPending}
 *   error={query.isError ? { onRetry: () => query.refetch() } : null}
 *   sort={sort}
 *   onSortChange={setSort}
 *   pagination={{ page, pageSize: 20, total: query.data?.total ?? 0 }}
 *   onPageChange={setPage}
 *   onRowClick={(v) => navigate(v.id)}
 * />
 * ```
 */
export function DataTable<TRow>({
  columns,
  rows,
  rowKey,
  caption,
  isLoading = false,
  error = null,
  emptyState,
  sort,
  onSortChange,
  pagination,
  onPageChange,
  renderMobileRow,
  onRowClick,
  className,
}: DataTableProps<TRow>) {
  if (error) {
    return (
      <ErrorState
        description={error.description ?? 'Die Liste konnte nicht geladen werden.'}
        {...(error.details !== undefined ? { details: error.details } : {})}
        {...(error.onRetry !== undefined ? { onRetry: error.onRetry } : {})}
      />
    )
  }

  if (isLoading) {
    return <LoadingSkeleton variant="table" rows={8} columns={columns.length} />
  }

  if (rows.length === 0) {
    return <>{emptyState ?? <EmptyState title="Keine Einträge vorhanden" />}</>
  }

  function toggleSort(key: string) {
    if (!onSortChange) return
    const direction: SortDirection =
      sort?.key === key && sort.direction === 'asc' ? 'desc' : 'asc'
    onSortChange({ key, direction })
  }

  const mobileSortOptions = columns.flatMap((column) => {
    if (!column.sortable) return []
    const label = column.mobileSortLabel ??
      (typeof column.header === 'string' ? column.header : null)
    if (!label) return []
    return [
      { value: `${column.key}:asc`, label: `${label} – aufsteigend` },
      { value: `${column.key}:desc`, label: `${label} – absteigend` },
    ]
  })

  function selectMobileSort(value: string) {
    if (!onSortChange) return
    const separator = value.lastIndexOf(':')
    if (separator <= 0) return
    const key = value.slice(0, separator)
    const direction = value.slice(separator + 1)
    if (direction !== 'asc' && direction !== 'desc') return
    onSortChange({ key, direction })
  }

  const table = (
    <Table>
      {caption && <caption className="sr-only">{caption}</caption>}
      <TableHeader>
        <TableRow>
          {columns.map((column) => {
            const activeSort = sort && sort.key === column.key ? sort : undefined
            return (
              <TableHead
                key={column.key}
                aria-sort={
                  activeSort
                    ? activeSort.direction === 'asc'
                      ? 'ascending'
                      : 'descending'
                    : undefined
                }
                className={cn(
                  column.align === 'right' && 'text-right',
                  column.className,
                )}
              >
                {column.sortable && onSortChange ? (
                  <button
                    type="button"
                    onClick={() => toggleSort(column.key)}
                    className={cn(
                      'inline-flex items-center gap-1 rounded-sm font-medium hover:text-foreground focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring',
                      activeSort ? 'text-foreground' : 'text-muted-foreground',
                      column.align === 'right' && 'flex-row-reverse',
                    )}
                  >
                    {column.header}
                    {activeSort ? (
                      activeSort.direction === 'asc' ? (
                        <ArrowUpIcon className="size-3.5" aria-hidden="true" />
                      ) : (
                        <ArrowDownIcon className="size-3.5" aria-hidden="true" />
                      )
                    ) : (
                      <ArrowUpDownIcon
                        className="size-3.5 opacity-50"
                        aria-hidden="true"
                      />
                    )}
                    <span className="sr-only">
                      {activeSort
                        ? activeSort.direction === 'asc'
                          ? ', aufsteigend sortiert. Klicken für absteigend.'
                          : ', absteigend sortiert. Klicken für aufsteigend.'
                        : ', nicht sortiert. Klicken zum Sortieren.'}
                    </span>
                  </button>
                ) : (
                  column.header
                )}
              </TableHead>
            )
          })}
        </TableRow>
      </TableHeader>
      <TableBody className="[&_tr]:border-border/60 [&_tr]:focus-within:bg-accent/60 [&_tr:nth-child(even)]:bg-muted/20 [&_tr]:hover:bg-accent/60">
        {rows.map((row) => {
          const key = rowKey(row)
          return (
            <TableRow
              key={key}
              data-row-key={key}
              {...(onRowClick
                ? {
                    tabIndex: 0,
                    role: 'button',
                    onClick: () => onRowClick(row),
                    onKeyDown: (event: React.KeyboardEvent<HTMLTableRowElement>) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        onRowClick(row)
                      }
                    },
                    className:
                      'cursor-pointer focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring',
                  }
                : {})}
            >
              {columns.map((column) => (
                <TableCell
                  key={column.key}
                  className={cn(
                    column.align === 'right' && 'text-right',
                    column.className,
                  )}
                >
                  {column.cell(row)}
                </TableCell>
              ))}
            </TableRow>
          )
        })}
      </TableBody>
    </Table>
  )

  return (
    <div className={cn('space-y-3', className)}>
      {renderMobileRow && sort && onSortChange && mobileSortOptions.length > 0 ? (
        <div className="space-y-1.5 md:hidden">
          <span className="text-sm font-medium">Sortieren nach</span>
          <Select
            value={`${sort.key}:${sort.direction}`}
            onValueChange={selectMobileSort}
          >
            <SelectTrigger aria-label="Sortierung" className="w-full">
              <SelectValue placeholder="Sortierung wählen" />
            </SelectTrigger>
            <SelectContent>
              {mobileSortOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      ) : null}

      {renderMobileRow ? (
        <>
          <div
            data-slot="data-table-desktop"
            className="hidden overflow-x-auto rounded-lg border md:block"
          >
            {table}
          </div>
          <ul className="divide-y rounded-lg border md:hidden">
            {rows.map((row) => {
              const key = rowKey(row)
              return (
                <li key={key} data-row-key={key} className="min-w-0 p-4">
                  {renderMobileRow(row)}
                </li>
              )
            })}
          </ul>
        </>
      ) : (
        <div className="overflow-x-auto rounded-lg border">{table}</div>
      )}

      {pagination && onPageChange && (
        <Pagination
          page={pagination.page}
          pageSize={pagination.pageSize}
          total={pagination.total}
          onPageChange={onPageChange}
          aria-label="Tabellenseiten"
        />
      )}
    </div>
  )
}
